// Work-surface telemetry: what people did, not just which URLs were fetched.
//
// Two halves, both landing in the AuditLogs table the Super Assistant reads
// through `work_activity`:
//
//   tracked()    wraps every write the work services expose. Records
//                work_<verb> with the item (source, id, title, client) and the
//                change, or work_<verb>_failed with the error. Request and
//                to-do writes go to Integrations and Chat, which log nothing
//                themselves, so this is their only record.
//
//   installWorkInteractionTracking()
//                one delegated listener set on document, active only on work
//                pages: clicks (by accessible name), tab switches, menu
//                picks, checkboxes, search/filter typing (debounced, the final
//                value), dialogs opened and closed (with how long), and time
//                spent on each work page. Nothing typed into a note, a
//                description or any field that is not a search/filter is kept.
//
// Everything is best-effort: a telemetry fault never breaks a click or a save.

import { queueEvent } from './auditService';

export const WORK_PAGE_PREFIXES = ['/work', '/cs', '/meetings', '/implementations', '/admin/work-settings', '/admin/cs-digest'];

export const isWorkPath = (path = '') => {
    const p = String(path).split('?')[0];
    return WORK_PAGE_PREFIXES.some((w) => p === w || p.startsWith(`${w}/`));
};

const clip = (v, n = 120) => {
    const s = String(v ?? '').replace(/\s+/g, ' ').trim();
    return s.length > n ? `${s.slice(0, n)}…` : s;
};

/** The facts about a work item worth keeping on every event about it. */
export const itemRef = (item) => {
    if (!item || typeof item !== 'object') return {};
    const out = {
        source: item.source, item_id: item.id != null ? String(item.id) : undefined,
        title: item.title ? clip(item.title, 200) : undefined,
        client: item.clientTitle || undefined, client_id: item.clientId || undefined,
        status: item.status || undefined, type: item.type || undefined,
    };
    Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
    return out;
};

/** The id a create call returned, whichever casing the service uses. */
export const newId = (r) => {
    const v = r?.id ?? r?.Id ?? r?.NexusSuccessRequest_Id ?? r?.todo?.id ?? r?.request?.id;
    return v != null ? String(v) : '';
};

export const itemResource = (item) => (item?.source ? `${item.source}:${item.id}` : 'work');

// Writes made inside a tracked verb are part of it: setWorkPeople on a request
// calls updateRequestFields, which calls updateSuccessRequest. Only the outer
// one is recorded, so a count of work_set_people is a count of people changes.
let depth = 0;

/**
 * Wrap a write. `describe(args, result)` returns { resource, details } for the
 * event; it runs after the write so it can include what came back (a new id).
 */
export function tracked(verb, fn, describe = () => ({})) {
    return async function trackedWrite(...args) {
        const outer = depth === 0;
        depth += 1;
        const started = Date.now();
        try {
            const result = await fn(...args);
            if (outer) record(verb, describe, args, result, started, null);
            return result;
        } catch (err) {
            if (outer) record(verb, describe, args, undefined, started, err);
            throw err;
        } finally {
            depth -= 1;
        }
    };
}

function record(verb, describe, args, result, started, err) {
    try {
        let d = {};
        try { d = describe(args, result) || {}; } catch { d = {}; }
        const details = {
            ...(d.details || {}),
            page: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
            ms: Date.now() - started,
        };
        if (err) details.error = clip(err?.message || err, 300);
        queueEvent(`work_${verb}${err ? '_failed' : ''}`, d.resource || 'work', details, { now: true });
    } catch {
        // never let telemetry surface as a failed save
    }
}

// ---------------------------------------------------------------------------
// Interactions
// ---------------------------------------------------------------------------

const INTERACTIVE = [
    'button', 'a[href]', 'summary', 'select',
    '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="menuitemcheckbox"]',
    '[role="option"]', '[role="checkbox"]', '[role="switch"]', '[role="radio"]', '[role="link"]',
    'input[type="checkbox"]', 'input[type="radio"]', '.MuiChip-clickable', '[data-track]',
].join(',');

// innerText is what a reader sees; textContent where there is no layout (tests, detached nodes).
const txt = (el) => (el ? (el.innerText || el.textContent || '') : '');

const SEARCHY = /search|filter|find|query|look ?up/i;

const kindOf = (el) => {
    const role = el.getAttribute('role');
    if (role === 'tab') return 'tab';
    if (role === 'menuitem' || role === 'menuitemcheckbox') return 'menu';
    if (role === 'option') return 'option';
    if (role === 'checkbox' || role === 'switch' || el.type === 'checkbox') return 'toggle';
    if (role === 'radio' || el.type === 'radio') return 'radio';
    if (el.tagName === 'A' || role === 'link') return 'link';
    if (el.classList?.contains('MuiChip-clickable')) return 'chip';
    return 'click';
};

const labelOf = (el) => clip(
    el.getAttribute('data-track')
    || el.getAttribute('aria-label')
    || el.getAttribute('title')
    || (el.labels && txt(el.labels[0]))
    || txt(el)
    || el.value
    || el.getAttribute('name')
    || el.getAttribute('data-testid')
    || el.tagName.toLowerCase(),
    80,
);

const dialogTitle = (dlg) => {
    if (!dlg) return '';
    const id = dlg.getAttribute('aria-labelledby');
    const t = (id && document.getElementById(id)) || dlg.querySelector('h1,h2,h3,.MuiDialogTitle-root');
    return clip(txt(t) || dlg.getAttribute('aria-label') || 'dialog', 80);
};

/** Where on the page this happened: the dialog it is in, else the nearest named region/heading. */
const areaOf = (el) => {
    const dlg = el.closest('[role="dialog"]');
    if (dlg) return `dialog: ${dialogTitle(dlg)}`;
    const named = el.closest('[data-track-area],[aria-label][role="region"],[role="tabpanel"],section[aria-label]');
    if (named) {
        return clip(named.getAttribute('data-track-area') || named.getAttribute('aria-label')
            || (named.getAttribute('aria-labelledby') && txt(document.getElementById(named.getAttribute('aria-labelledby'))))
            || '', 80);
    }
    return '';
};

const path = () => window.location.pathname + window.location.search;
const onWork = () => isWorkPath(window.location.pathname);

const ui = (details) => queueEvent('ui_event', path(), details);

let installed = false;

export function installWorkInteractionTracking() {
    if (installed || typeof document === 'undefined') return () => {};
    installed = true;

    const onClick = (e) => {
        if (!onWork()) return;
        const el = e.target?.closest?.(INTERACTIVE);
        if (!el) return;
        const kind = kindOf(el);
        const details = { kind, label: labelOf(el), area: areaOf(el) };
        if (kind === 'toggle') {
            const checked = el.type === 'checkbox' ? el.checked : el.getAttribute('aria-checked') === 'true';
            // a native checkbox has already flipped by the time click fires; an aria one flips after
            details.on = el.type === 'checkbox' ? checked : !checked;
        }
        if (kind === 'link' && el.getAttribute('href')) details.href = clip(el.getAttribute('href'), 200);
        if (el.getAttribute('aria-selected') === 'true' && kind === 'tab') details.already_selected = true;
        ui(details);
    };

    // Search and filter boxes: the settled value, once typing pauses.
    const typing = new WeakMap();
    const onInput = (e) => {
        if (!onWork()) return;
        const el = e.target;
        if (!el || el.tagName !== 'INPUT') return;
        const type = (el.type || 'text').toLowerCase();
        if (!['text', 'search'].includes(type)) return;
        const hint = `${type} ${el.getAttribute('placeholder') || ''} ${el.getAttribute('aria-label') || ''} ${el.name || ''} ${el.id || ''}`;
        if (type !== 'search' && !SEARCHY.test(hint)) return;
        clearTimeout(typing.get(el));
        typing.set(el, setTimeout(() => {
            ui({ kind: 'search', label: labelOf(el) || clip(el.getAttribute('placeholder'), 80), value: clip(el.value, 100), area: areaOf(el) });
        }, 1200));
    };

    const onChange = (e) => {
        if (!onWork()) return;
        const el = e.target;
        if (el?.tagName === 'SELECT') {
            ui({ kind: 'select', label: labelOf(el), value: clip(el.options[el.selectedIndex]?.text || el.value, 80), area: areaOf(el) });
        } else if (el?.tagName === 'INPUT' && el.type === 'date') {
            ui({ kind: 'date', label: labelOf(el), value: el.value, area: areaOf(el) });
        }
    };

    // Dialogs: opened, and closed with how long they were open.
    const openDialogs = new Map();
    const scan = () => {
        const now = new Set(document.querySelectorAll('[role="dialog"]'));
        now.forEach((d) => {
            if (!openDialogs.has(d) && onWork()) {
                openDialogs.set(d, { at: Date.now(), title: dialogTitle(d) });
                ui({ kind: 'dialog_open', label: openDialogs.get(d).title });
            }
        });
        openDialogs.forEach((v, d) => {
            if (!now.has(d) || !d.isConnected) {
                openDialogs.delete(d);
                ui({ kind: 'dialog_close', label: v.title, open_ms: Date.now() - v.at });
            }
        });
    };
    let scanQueued = false;
    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => {
        if (scanQueued) return;
        scanQueued = true;
        setTimeout(() => { scanQueued = false; scan(); }, 150);
    }) : null;

    document.addEventListener('click', onClick, true);
    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onChange, true);
    observer?.observe(document.body, { childList: true, subtree: true });

    return () => {
        document.removeEventListener('click', onClick, true);
        document.removeEventListener('input', onInput, true);
        document.removeEventListener('change', onChange, true);
        observer?.disconnect();
        installed = false;
    };
}

// Time on each work page: recorded when the route leaves it.
let current = null;
export function noteRoute(fullPath) {
    const now = Date.now();
    if (current && isWorkPath(current.path)) {
        queueEvent('ui_event', current.path, { kind: 'view_end', label: current.path.split('?')[0], active_ms: now - current.at, next: fullPath });
    }
    current = { path: fullPath, at: now };
}

/** Explain-mode hovers: which explanation was read, deduped per key for 30s. */
const explained = new Map();
export function noteExplain(key) {
    if (!key || !onWork()) return;
    const last = explained.get(key) || 0;
    if (Date.now() - last < 30000) return;
    explained.set(key, Date.now());
    ui({ kind: 'explain_hover', label: String(key) });
}
